import { ApolloClient } from '@apollo/client';
import { CancelReason, ParkingSessionStatus } from '@/graphql/generated';
import { PARKING_SESSION_LIST_ITEM_QUERY } from './parking-sessions.gql';
import { ParkingSessionListItem } from './types';
import {
	calculateDuration,
	formatCustomerName,
	getParkingSessionDisplayStatus
} from './utils';

interface ParkingSessionNode {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	exitDateTime?: string | null;
	paymentDateTime?: string | null;
	paymentAmount?: number | null;
	vehicleId?: string | null;
	parkingSessionTypeId?: number | null;
	paymentMethodId?: number | null;
	customerId?: string | null;
	parkingPositionId?: string | null;
	contractId?: string | null;
	status: ParkingSessionStatus;
	cancelReason?: CancelReason | null;
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
		make?: string | null;
		model?: string | null;
		color?: string | null;
		vehicleType?: {
			name?: string | null;
		} | null;
	} | null;
	parkingSessionType?: {
		parkingSessionTypeId: number;
		name?: string | null;
	} | null;
	paymentMethod?: {
		paymentMethodId: number;
		name?: string | null;
	} | null;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
	} | null;
	parkingPosition?: {
		parkingPositionId: string;
		code?: string | null;
	} | null;
	contract?: {
		contractId: string;
		beginDate?: string | null;
		endDate?: string | null;
	} | null;
}

interface ParkingSessionListItemResponse {
	parkingSession?: ParkingSessionNode | null;
}

export interface SelectParkingSessionAfterSaveOptions {
	parkingSessionId: string;
	client: ApolloClient<any>;
	currentParkingSessions: ParkingSessionListItem[];
	onDataUpdate: (updatedSessions: ParkingSessionListItem[]) => void;
	onSessionSelect: (session: ParkingSessionListItem) => void;
}

function mapNodeToListItem(node: ParkingSessionNode): ParkingSessionListItem {
	const durationMinutes = calculateDuration(
		node.entryDateTime,
		node.exitDateTime
	);

	const displayStatus = getParkingSessionDisplayStatus(node.status);

	return {
		parkingSessionId: node.parkingSessionId,
		ticketId: node.ticketId,
		entryDateTime: node.entryDateTime,
		exitDateTime: node.exitDateTime,
		paymentDateTime: node.paymentDateTime,
		paymentAmount: node.paymentAmount,
		vehicleId: node.vehicleId,
		vehiclePlateNumber: node.vehicle?.plateNumber ?? 'Unknown Vehicle',
		vehicleMake: node.vehicle?.make ?? undefined,
		vehicleModel: node.vehicle?.model ?? undefined,
		vehicleColor: node.vehicle?.color ?? undefined,
		vehicleTypeName: node.vehicle?.vehicleType?.name ?? undefined,
		parkingSessionType: node.parkingSessionType?.name ?? undefined,
		paymentMethod: node.paymentMethod?.name ?? undefined,
		customerId: node.customerId,
		customerName: formatCustomerName(
			node.customer?.isCompany,
			node.customer?.firmName,
			node.customer?.firstName,
			node.customer?.lastName
		),
		parkingPositionCode: node.parkingPosition?.code ?? undefined,
		contractId: node.contractId,
		durationMinutes: durationMinutes ?? undefined,
		status: node.status,
		cancelReason: node.cancelReason,
		displayStatus
	};
}

/**
 * Fetches the latest parking session data from the server and updates the list,
 * then selects and highlights the session in the grid and details panel.
 *
 * @param options Configuration options for selecting the parking session
 * @returns Promise that resolves when the operation is complete
 */
export async function selectParkingSessionAfterSave(
	options: SelectParkingSessionAfterSaveOptions
): Promise<void> {
	const {
		parkingSessionId,
		client,
		currentParkingSessions,
		onDataUpdate,
		onSessionSelect
	} = options;

	try {
		// Step 1: Fetch the latest parking session data from the server
		const { data } =
			await client.query<ParkingSessionListItemResponse>({
				query: PARKING_SESSION_LIST_ITEM_QUERY,
				variables: { parkingSessionId },
				fetchPolicy: 'network-only' // Always fetch fresh data from server
			});

		const fetchedNode = data?.parkingSession;

		if (!fetchedNode) {
			console.warn(
				`Parking session with ID ${parkingSessionId} not found after save`
			);
			return;
		}

		// Map the fetched node to ParkingSessionListItem format
		const fetchedSession = mapNodeToListItem(fetchedNode);

		// Step 2: Check if session exists in current list
		const existingIndex = currentParkingSessions.findIndex(
			s => s.parkingSessionId === parkingSessionId
		);

		let updatedSessions: ParkingSessionListItem[];

		if (existingIndex >= 0) {
			// Session exists - replace with updated data
			updatedSessions = [...currentParkingSessions];
			updatedSessions[existingIndex] = fetchedSession;
		} else {
			// New session - prepend to the list (newest first)
			updatedSessions = [fetchedSession, ...currentParkingSessions];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedSessions);

		// Step 4: Select the session (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onSessionSelect(fetchedSession);
		}, 50);
	} catch (error) {
		console.error('Error selecting parking session after save:', error);
		throw error;
	}
}
